/**
 * Server-side 301 redirect map for retired URLs.
 *
 * Ported from public/_redirects (the validated source of truth), with two
 * defects deliberately NOT ported:
 *   (a) the seven /louisville-ky/* rules that sat below the SPA catch-all and
 *       were therefore dead — they live in the lookup table here, so ordering
 *       cannot silence them again;
 *   (b) /about/faq/ pointed at /about — trailing-slash normalisation now always
 *       lands on the page's own path.
 *
 * Resolver semantics:
 *   1. Trailing slash is trimmed first, but never returned early — the trimmed
 *      path runs the full match chain, and the bare trimmed path is only used
 *      as a fallback when nothing matched.
 *   2. Order: exact -> WordPress file -> *.php -> longest-prefix.
 *   3. Splat (carry the remainder onto the destination) is an explicit flag,
 *      never inferred from the destination string.
 *   4. One hop only: no destination may itself be a redirect source.
 */

/** Exact-path redirects. Keyed lookup — order is irrelevant by construction. */
export const EXACT_REDIRECTS: Record<string, string> = {
  // Path changes — old WP slug to new slug
  "/contact-us": "/contact",
  "/contact-us-confirmation": "/contact",
  "/electrical-services/electrical-contracting": "/electrical-services/commercial-electrical-contractor",
  "/electrical-services/service-repair": "/electrical-services/electrical-service-and-repair",
  "/electrical-services/building-automation-systems": "/hvac-services/building-automation-systems",
  "/energy-management/guaranteed-energy-savings-financing": "/energy-management/guaranteed-energy-savings",
  "/electrical-services/switchgear-services": "/electrical-services/switchgear-installation",
  "/electrical-services/residential-electrical": "/electrical-services",
  "/energy-management/led-lighting-upgrades": "/energy-management/lighting-retrofits",

  // Duplicate/old slug redirects
  "/industries/data-centers": "/industries/data-center-electrical",
  "/industries/healthcare-facilities": "/industries/healthcare-facility-compliance",
  "/industries/hospitality-hotels": "/industries/hotel-electrical-contractor",
  "/industries/education-schools": "/industries/school-electrical-contractor",
  "/stadium-sports-lighting/park-recreation-lighting": "/stadium-sports-lighting/recreational-park-lighting",
  "/stadium-sports-lighting/arena-lighting": "/stadium-sports-lighting/arena-event-lighting",
  "/stadium-sports-lighting/basketball-court-lighting": "/stadium-sports-lighting/basketball-arena-lighting",
  "/energy-management/energy-consulting": "/energy-management/energy-efficiency-consulting",
  "/energy-management/utility-bill-evaluation": "/energy-management/energy-bill-analysis",

  // Old /services/* → /industries/*
  "/services/hotel-electrical-contractor": "/industries/hotel-electrical-contractor",
  "/services/automotive-plant-electrical": "/industries/automotive-plant-electrical",
  "/services/restaurant-electrical": "/industries/restaurant-electrical",
  "/services/manufacturing-electrical": "/industries/manufacturing-electrical",
  "/services/warehouse-electrical": "/industries/warehouse-electrical",
  "/services/healthcare-facility-compliance": "/industries/healthcare-facility-compliance",
  "/services/school-electrical-contractor": "/industries/school-electrical-contractor",
  "/services/church-electrical-contractor": "/industries/church-electrical-contractor",
  "/services/university-electrical": "/industries/university-electrical",
  "/services/municipal-electrical": "/industries/municipal-electrical",
  "/services/water-treatment-electrical": "/industries/water-treatment-electrical",
  "/services/data-center-electrical": "/industries/data-center-electrical",
  "/services/food-beverage-manufacturing": "/industries/food-beverage-manufacturing",
  "/services/stadium-arena-electrical": "/industries/stadium-arena-electrical",
  "/services": "/industries",

  // Legacy blog paths
  "/blogs": "/blog",
  "/our-blog": "/blog",
  "/feed": "/blog",

  // Retired WordPress pages
  "/sample-page": "/",

  // Stadium lighting
  "/stadium-lighting": "/stadium-sports-lighting",

  // Ohio service-area pages — outside territory
  "/service-area/cincinnati-oh": "/service-area",
  "/service-area/lebanon-oh": "/service-area",
  "/service-area/mason-oh": "/service-area",
  "/service-area/hamilton-oh": "/service-area",
  "/service-area/fairfield-oh": "/service-area",
  "/service-area/wilmington-oh": "/service-area",
  "/service-area/west-chester-oh": "/service-area",
  "/service-area/middletown-oh": "/service-area",

  // HVAC legacy landing pages
  "/hvac": "/hvac-services",
  "/heating": "/hvac-services",
  "/cooling": "/hvac-services",
  "/furnace": "/hvac-services",

  // Legacy marketing pages
  "/about-us": "/about",
  "/about-hadley-energy-solutions": "/about",
  "/service-areas": "/service-area",
  "/commercial-electrical": "/electrical-services/commercial-electrical-contractor",
  "/commercial-electrical-services": "/electrical-services/commercial-electrical-contractor",
  "/commercial-hvac": "/hvac-services/commercial-hvac",
  "/energy-audit": "/energy-management/energy-audits",
  "/hvac-maintenance": "/hvac-services/preventative-hvac-maintenance",
  "/hvac-maintenance-louisville-ky": "/hvac-services/preventative-hvac-maintenance",

  // Consolidated guide pages
  "/guide/24-hour-emergency-electrician-louisville-ky": "/electrical-services/emergency-electrical-service",
  "/guide/emergency-electrician-louisville-ky": "/electrical-services/emergency-electrical-service",
  "/guide/commercial-electrical-contractor-louisville-2026": "/electrical-services/commercial-electrical-contractor",
  "/guide/hvac-contractor-louisville-ky": "/hvac-services",
  "/guide/heating-cooling-louisville-ky": "/hvac-services",

  // Retired /louisville-ky/* landing pages (dead in _redirects — live here)
  "/louisville-ky/emergency-electrician": "/electrical-services/emergency-electrical-service",
  "/louisville-ky/generator-installation": "/electrical-services/generator-installation",
  "/louisville-ky/hvac-contractor": "/hvac-services",
  "/louisville-ky/commercial-electrician": "/electrical-services/commercial-electrical-contractor",
  "/louisville-ky/lighting-contractor": "/energy-management/lighting-retrofits",
  "/louisville-ky/air-conditioning-contractor": "/hvac-services/ac-installation-repair",
  "/louisville-ky": "/service-area/louisville-ky",
};

/** WordPress entry-point files. */
export const WP_FILE_REDIRECTS: Record<string, string> = {
  "/wp-login.php": "/",
  "/wp-signup.php": "/",
  "/xmlrpc.php": "/",
};

/** Prefix rules. `splat: true` carries the remainder onto the destination. */
export const PREFIX_REDIRECTS: { prefix: string; to: string; splat?: boolean }[] = [
  { prefix: "/stadium-lighting/", to: "/stadium-sports-lighting/", splat: true },
  { prefix: "/services/", to: "/industries" },
  { prefix: "/author/", to: "/about" },
  { prefix: "/2023/", to: "/blog" },
  { prefix: "/wp-content/", to: "/" },
  { prefix: "/wp-admin/", to: "/" },
  { prefix: "/wp-includes/", to: "/" },
  { prefix: "/wp-json/", to: "/" },
  { prefix: "/feed/", to: "/blog" },
  { prefix: "/category/", to: "/blog" },
  { prefix: "/tag/", to: "/blog" },
  { prefix: "/page/", to: "/" },
  { prefix: "/comments/", to: "/" },
  { prefix: "/trackback/", to: "/" },
  { prefix: "/louisville-ky/", to: "/service-area/louisville-ky" },
];

/**
 * Resolve a pathname to its 301 destination, or null when the path is not a
 * retired URL. Never returns the input path (no self-redirect loops).
 */
export function resolveRedirect(pathname: string): string | null {
  if (!pathname.startsWith("/")) return null;

  // 1. Normalise the trailing slash, but keep matching on the trimmed path.
  const trimmed = pathname.length > 1 && pathname.endsWith("/") ? pathname.replace(/\/+$/, "") : pathname;
  const path = trimmed === "" ? "/" : trimmed;

  // 2a. Exact match.
  const exact = EXACT_REDIRECTS[path];
  if (exact) return exact;

  // 2b. WordPress file match.
  const wpFile = WP_FILE_REDIRECTS[path];
  if (wpFile) return wpFile;

  // 2c. Any other *.php request.
  if (path.endsWith(".php")) return "/";

  // 2d. Longest-prefix match.
  let best: { prefix: string; to: string; splat?: boolean } | null = null;
  for (const rule of PREFIX_REDIRECTS) {
    if (path.startsWith(rule.prefix) && (!best || rule.prefix.length > best.prefix.length)) {
      best = rule;
    }
  }
  if (best) {
    if (best.splat) {
      const remainder = path.slice(best.prefix.length);
      const dest = best.to.endsWith("/") ? best.to + remainder : `${best.to}/${remainder}`;
      return dest === path ? null : dest;
    }
    return best.to === path ? null : best.to;
  }

  // 3. Fallback: trailing-slash normalisation onto the page's own path.
  if (path !== pathname) return path;

  return null;
}
